import React, { useState } from 'react';
import { Shield, Lock, Eye, AlertTriangle, Wifi, Fingerprint, Key, UserCheck, Clock, ArrowRight, CheckCircle, Star, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import Breadcrumb from './Breadcrumb';
import Footer from './Footer';

const SecurityPage = ({ setCurrentSection, onSelectGuide, guides }) => {
  // Organiser les guides par domaines de sécurité
  const toolCategories = {
    passwords: {
      name: 'Mots de passe',
      icon: Lock,
      description: 'Gestion sécurisée des identifiants et authentification',
      gradient: 'from-red-500 to-pink-600',
      guides: ['password-security']
    },
    phishing: {
      name: 'Anti-phishing',
      icon: AlertTriangle,
      description: 'Protection contre les tentatives d\'hameçonnage',
      gradient: 'from-orange-500 to-red-600',
      guides: ['phishing-protection']
    },
    wifi: {
      name: 'Sécurité WiFi',
      icon: Wifi,
      description: 'Connexions réseau sécurisées',
      gradient: 'from-blue-500 to-indigo-600',
      guides: ['wifi-security']
    },
    twofa: {
      name: 'Authentification 2FA',
      icon: Fingerprint,
      description: 'Double authentification et sécurité renforcée',
      gradient: 'from-purple-500 to-indigo-600',
      guides: ['2fa-authentication']
    },
    privacy: {
      name: 'Vie privée',
      icon: Eye,
      description: 'Protection des données personnelles',
      gradient: 'from-green-500 to-teal-600',
      guides: []
    },
    backup: {
      name: 'Sauvegarde',
      icon: Key,
      description: 'Protection et récupération des données',
      gradient: 'from-teal-500 to-cyan-600',
      guides: []
    }
  };

  // Function to count procedures in a guide
  const countProceduresInGuide = (guideId) => {
    const guide = guides.find(g => g.id === guideId);
    if (!guide || !guide.content || !guide.content.sections) return 0;
    
    let linkCount = 0;
    guide.content.sections.forEach(section => {
      if (section.content) {
        const links = section.content.match(/\[([^\]]+)\]\(([^)]+)\)/g);
        if (links) {
          linkCount += links.length;
        }
      }
    });
    return linkCount;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-100">
      {/* Breadcrumb */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb />
        </div>
      </div>

      {/* Hero Banner */}
      <section className="relative overflow-hidden bg-gradient-to-r from-red-600 via-orange-600 to-yellow-600">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white bg-opacity-20 backdrop-blur-sm rounded-3xl mb-8">
              <Shield className="h-12 w-12 text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-6">
              Sécurité & Protection
            </h1>
            <p className="text-xl md:text-2xl text-red-100 max-w-3xl mx-auto">
              Protégez-vous contre les cybermenaces avec nos guides de sécurité professionnels
            </p>
          </div>
        </div>
        
        {/* Floating elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-red-300 rounded-full opacity-20 animate-bounce"></div>
        <div className="absolute bottom-20 right-10 w-16 h-16 bg-orange-300 rounded-full opacity-30 animate-pulse"></div>
        <div className="absolute top-1/2 left-1/4 w-12 h-12 bg-yellow-300 rounded-full opacity-25 animate-ping"></div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Security Tools Section */}
        <section className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-3xl p-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-400 to-red-500 rounded-2xl mb-6">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Domaines de Sécurité
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Protégez-vous efficacement avec nos guides spécialisés par domaine
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(toolCategories).map(([key, category]) => {
              const IconComponent = category.icon;
              const hasGuides = category.guides.length > 0;
              const firstGuideId = hasGuides ? category.guides[0] : null;
              const procedureCount = hasGuides ? countProceduresInGuide(firstGuideId) : 0;
              
              return (
                <Card 
                  key={key} 
                  className="group cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 border-0 overflow-hidden relative h-full flex flex-col"
                  onClick={() => {
                    if (hasGuides && onSelectGuide && firstGuideId) {
                      console.log('Security Guide selected:', { id: firstGuideId });
                      onSelectGuide({ id: firstGuideId });
                    }
                  }}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${category.gradient} opacity-5 group-hover:opacity-10 transition-opacity duration-300`}></div>
                  
                  <CardHeader className="text-center pb-4 relative z-10 flex-grow">
                    <div className={`mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br ${category.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900 mb-2">
                      {category.name}
                    </CardTitle>
                    <CardDescription className="text-gray-600 leading-relaxed flex-grow">
                      {category.description}
                    </CardDescription>
                    
                    {hasGuides && procedureCount > 0 && (
                      <div className="mt-4 flex items-center justify-center">
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          {procedureCount} procédure{procedureCount > 1 ? 's' : ''}
                        </Badge>
                      </div>
                    )}
                    
                    {hasGuides && procedureCount === 0 && (
                      <div className="mt-4 flex items-center justify-center">
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          1 guide
                        </Badge>
                      </div>
                    )}
                  </CardHeader>
                  
                  {/* Bottom Action Bar */}
                  <div className={`bg-gray-900 ${hasGuides ? `group-hover:bg-gradient-to-r group-hover:${category.gradient}` : ''} transition-all duration-300 p-4 relative z-10`}>
                    <Button 
                      variant="ghost"
                      disabled={!hasGuides}
                      className={`w-full font-bold text-lg hover:bg-transparent ${
                        hasGuides 
                          ? 'text-white' 
                          : 'text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {hasGuides ? 'Consulter le guide' : 'Bientôt disponible'}
                      {hasGuides && <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />}
                    </Button>
                  </div>
                  
                  {hasGuides && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-1000 pointer-events-none"></div>
                  )}
                </Card>
              );
            })}
          </div>
        </section>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default SecurityPage;